import { useState, useEffect, useCallback } from "react";
import { Plus, Clock, Users, CheckCircle2, AlertCircle, FileText, Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { ContractorApi } from "api/contractors/service";
import { AttendanceModal } from "./AttendanceModal";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const Card = ({ className = "", children }) => (
    <div className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}>
        {children}
    </div>
);

const CardContent = ({ className = "", children }) => (
    <div className={`p-6 ${className}`}>{children}</div>
);

const Button = ({ variant = "default", size = "default", className = "", children, ...props }) => {
    const baseStyles = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
    const variants = {
        default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input bg-transparent shadow-sm hover:bg-sm-accent hover:text-sm-accent-foreground",
        secondary: "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
        ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
        link: "text-sm-primary underline-offset-4 hover:underline",
    };
    const sizes = {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
    };
    return (
        <button className={cn(baseStyles, variants[variant], sizes[size], className)} {...props}>
            {children}
        </button>
    );
};

const Badge = ({ variant = "default", className = "", children }) => {
    const baseStyles = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
    const variants = {
        default: "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
        secondary: "border-transparent bg-sm-secondary/10 text-sm-secondary hover:bg-sm-secondary/20",
        outline: "text-foreground",
    };
    return (
        <div className={cn(baseStyles, variants[variant], className)}>
            {children}
        </div>
    );
};

export function ContractorAttendanceTab({ contractor, workOrders }) {
    const [attendanceLogs, setAttendanceLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedWO, setSelectedWO] = useState(null);
    const [selectedAttendance, setSelectedAttendance] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [woDetails, setWoDetails] = useState({});

    const fetchAttendance = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await ContractorApi.getAttendanceByContractor(contractor.id);
            if (response.status) {
                setAttendanceLogs(response.data || []);
            }
        } catch (err) {
            console.error("Error fetching attendance:", err);
        } finally {
            setIsLoading(false);
        }
    }, [contractor.id]);

    useEffect(() => {
        fetchAttendance();
    }, [fetchAttendance]);

    const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()

    const activeWOsToday = workOrders.filter(wo => {
        const start = new Date(wo.startDate);
        const end = new Date(wo.endDate);
        const current = new Date(today);
        // Remove wo.status === "Active" if only active dates matter, but usually status also matters
        return current >= start && current <= end && wo.status === "Active";
    });

    const getAttendanceForWO = (woId) => {
        return attendanceLogs.find(log => log.work_order === woId && log.date === today);
    };

    const handleOpenModal = async (wo) => {
        setSelectedWO(wo);
        const existingAttendance = getAttendanceForWO(wo.workorder_id);
        setSelectedAttendance(existingAttendance);

        // Fetch WO details if not already cached to get scope items
        if (!woDetails[wo.workorder_id]) {
            try {
                const response = await ContractorApi.getWorkOrderDetail(wo.workorder_id);
                if (response.status) {
                    const mappedDetails = {
                        ...wo,
                        scopeItems: (response.data.scope_of_work || []).map(item => ({
                            id: item.id,
                            item: item.item_name,
                            description: item.description
                        }))
                    };
                    setWoDetails(prev => ({ ...prev, [wo.workorder_id]: mappedDetails }));
                }
            } catch (err) {
                console.error("Error fetching WO details:", err);
            }
        }

        setIsModalOpen(true);
    };

    const handleSubmitAttendance = async (data) => {
        try {
            setIsSubmitting(true);
            let response;
            if (selectedAttendance) {
                response = await ContractorApi.updateAttendance(selectedAttendance.id, data);
            } else {
                response = await ContractorApi.createAttendance({
                    ...data,
                    contractor_id: contractor.id,
                    work_order_id: selectedWO.workorder_id,
                });
            }

            if (response.status) {
                await fetchAttendance();
                setIsModalOpen(false);
            }
        } catch (err) {
            console.error("Error saving attendance:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    return (
        <div className="space-y-6">
            {/* Today's Tasks */}
            <div className="space-y-4">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4 text-sm-secondary" />
                    Active Work Orders Today
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeWOsToday.map((wo) => {
                        const attendance = getAttendanceForWO(wo.workorder_id);
                        return (
                            <Card key={wo.id} className="border-border/60 shadow-none hover:border-sm-primary/30 transition-colors">
                                <CardContent className="p-4 flex flex-col h-full">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-sm font-medium text-foreground truncate">{wo.projectName}</h3>
                                            <p className="text-xs text-muted-foreground">WO: #{wo.id}</p>
                                        </div>
                                        {attendance ? (
                                            <Badge variant="secondary" className={cn(
                                                "text-[10px] font-medium border-0",
                                                attendance.attendance_status === "Present"
                                                    ? "bg-green-500/10 text-green-600"
                                                    : "bg-red-500/10 text-red-600"
                                            )}>
                                                {attendance.attendance_status}
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-[10px] font-normal border-amber-500/30 text-amber-600 bg-amber-500/5">
                                                Pending
                                            </Badge>
                                        )}
                                    </div>

                                    <div className="mt-auto pt-4 flex items-center justify-between border-t border-border/40">
                                        <div className="flex items-center gap-3">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-muted-foreground uppercase">Workers</span>
                                                <span className="text-xs font-medium text-foreground">{attendance?.person_count || 0}</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-muted-foreground uppercase">Hours</span>
                                                <span className="text-xs font-medium text-foreground">{attendance?.hours || 0}</span>
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant={attendance ? "outline" : "default"}
                                            className="h-8 text-xs gap-1.5"
                                            onClick={() => handleOpenModal(wo)}
                                        >
                                            {attendance ? "Update" : "Mark Attendance"}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}

                    {activeWOsToday.length === 0 && (
                        <div className="col-span-full py-8 text-center bg-muted/20 rounded-lg border border-dashed border-border/60">
                            <AlertCircle className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground font-medium">No active work orders for today</p>
                            <p className="text-xs text-muted-foreground mt-1">Attendance can only be marked for today's active contracts</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Attendance History */}
            <div className="space-y-4">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-sm-secondary" />
                    Attendance History
                </h2>

                <Card className="border-border/60 shadow-none overflow-hidden">
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/30 border-b border-border/60">
                                <tr>
                                    <th className="h-10 px-4 text-left font-medium text-muted-foreground text-xs">Date</th>
                                    <th className="h-10 px-4 text-left font-medium text-muted-foreground text-xs">Work Order</th>
                                    <th className="h-10 px-4 text-center font-medium text-muted-foreground text-xs">Status</th>
                                    <th className="h-10 px-4 text-center font-medium text-muted-foreground text-xs">Labour</th>
                                    <th className="h-10 px-4 text-center font-medium text-muted-foreground text-xs">Hours</th>
                                    <th className="h-10 px-4 text-left font-medium text-muted-foreground text-xs">Scope Worked</th>
                                    <th className="h-10 px-4 text-left font-medium text-muted-foreground text-xs">Created By</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/40">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={7} className="p-8 text-center">
                                            <Loader2 className="h-6 w-6 animate-spin mx-auto text-sm-primary" />
                                        </td>
                                    </tr>
                                ) : attendanceLogs.length > 0 ? (
                                    attendanceLogs.map((log) => (
                                        <tr key={log.id} className="hover:bg-muted/20 transition-colors">
                                            <td className="p-4 text-sm whitespace-nowrap">{formatDate(log.date)}</td>
                                            <td className="p-4">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium">#{log.work_order_id}</span>
                                                    <span className="text-[10px] text-muted-foreground truncate max-w-[150px]">
                                                        ID: {log.work_order}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="p-4 text-center">
                                                <Badge variant="secondary" className={cn(
                                                    "text-[10px] border-0",
                                                    log.attendance_status === "Present"
                                                        ? "bg-green-500/10 text-green-600"
                                                        : "bg-red-500/10 text-red-600"
                                                )}>
                                                    {log.attendance_status}
                                                </Badge>
                                            </td>
                                            <td className="p-4 text-center text-sm font-medium">{log.person_count}</td>
                                            <td className="p-4 text-center text-sm font-medium">{log.hours}</td>
                                            <td className="p-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {log.scope_items?.map((item, idx) => (
                                                        <Badge key={idx} variant="outline" className="text-[10px] font-normal border-border/60 whitespace-nowrap">
                                                            {item.Item}
                                                        </Badge>
                                                    ))}
                                                    {(!log.scope_items || log.scope_items.length === 0) && (
                                                        <span className="text-xs text-muted-foreground">-</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                                                {log.created_by || "-"}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="p-12 text-center text-muted-foreground">
                                            <div className="flex flex-col items-center">
                                                <FileText className="h-8 w-8 text-muted-foreground/30 mb-2" />
                                                <p className="text-sm">No attendance records found</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            </div>

            {isModalOpen && (
                <AttendanceModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    workOrder={woDetails[selectedWO?.workorder_id] || selectedWO}
                    attendance={selectedAttendance}
                    onSubmit={handleSubmitAttendance}
                    isSubmitting={isSubmitting}
                />
            )}
        </div>
    );
}
